import { FC, PropsWithChildren, useEffect, useState } from "react";
import { ModalForm, Modal, Button } from "../../components/Modal";
import ModalHeading from "../../components/Modal/ModalHeading";
import SkeletonLoader from "../../Skeletons/IntakeFormSkeleton";
import InputWrapper from "../../components/Modal/InputWrapper";
import SliderBar from "../../components/Modal/SlideBar";
import TextAreaField from "../../components/Modal/TextAreaField";
import RadioButton from "../../components/Modal/RadioButton";
import CheckboxInput from "../../components/Modal/CheckBoxInput";
import PatientStyle from "../../styles/Patient.module.css";
import { formatLabel } from "../../utils/formatlabel";
import { useFetchCarePlan } from "../../hooks/patients";
import { IRtmTemplate } from "../../hooks/rtmTemplate/index.type";

// --- Type Definitions ---
interface QuestionOption {
  key: string;
  name: string;
  textboxRequired: "yes" | "no";
}

interface ScaleDetails {
  min: QuestionOption;
  max: QuestionOption;
}

export interface BaseQuestion {
  _id: string;
  answerType: "single_option" | "multiple_option" | "textarea" | "scale";
  option: QuestionOption[];
  questionDependable: "yes" | "no";
  dependableQuestionId: string;
  dependableQuestionType: string;
  dependableQuestionOption: string;
  sequence: number;
  hasQuestionDependOnThis: "yes" | "no";
  text: string;
  questionId: string;
  scale: ScaleDetails;
  createdAt: string;
  updatedAt: string;
  category?: {
    _id: string;
    key: string;
    name: string;
  };
}

export interface CategorizedQuestion extends BaseQuestion {
  category: {
    _id: string;
    key: string;
    name: string;
  };
}

export interface CategoryGroup {
  _id: string;
  name: string;
  records: CategorizedQuestion[];
}

export type CarePlanDetailsData = CategoryGroup[] | BaseQuestion[];

export interface CarePlanContent {
  templateName: string;
  questionGroupedByCategory: "yes" | "no";
  data: CarePlanDetailsData;
}

export interface CarePlanAPIResponse {
  status: string;
  data: CarePlanContent;
}
// --- End Type Definitions ---

export type AddEditModalProps = PropsWithChildren<{
  show: boolean;
  onClose?: () => void;
  templateData: IRtmTemplate;
}>;

// --- QuestionItem Component ---
interface QuestionItemProps {
  question: BaseQuestion;
  formState: { [key: string]: any };
  categoryTitle?: string;
}

const QuestionItem: FC<QuestionItemProps> = ({ question, formState, categoryTitle }) => {
  const answerType = question.answerType;
  const options = question.option || [];

  return (
    <div key={question._id} style={{ marginBottom: "1rem" }}>
      {answerType === "single_option" && (
        <InputWrapper
          subTitle={question.text}
          title={categoryTitle}
          type={formatLabel(answerType)}
        >
          <div
            style={{
              display: "flex",
              flexDirection: options.length < 2 ? "row" : "column",
              gap: "1rem",
              marginTop: "1rem",
            }}
          >
            {options.map((opt) => (
              <div key={opt.key}>
                <RadioButton
                  hideRadio={true}
                  label={opt.name}
                  checked={formState[question.questionId] === opt.name}
                />
                {opt.textboxRequired === "yes" &&
                  formState[question.questionId] === opt.name && (
                    <TextAreaField
                      rows={2}
                      placeholder="Please specify"
                      value={
                        formState[
                          `${question.questionId}_${opt.name}_textbox`
                        ] || ""
                      }
                    />
                  )}
              </div>
            ))}
          </div>
        </InputWrapper>
      )}

      {answerType === "multiple_option" && (
        <InputWrapper
          subTitle={question.text}
          title={categoryTitle}
          type={formatLabel(answerType)}
        >
          <div className={PatientStyle.checkbox_div_1}>
            {options.map((opt) => (
              <div key={opt.key}>
                <CheckboxInput
                  hideCheckbox={true}
                  label={opt.name}
                  checked={(formState[question.questionId] || []).includes(
                    opt.name
                  )}
                />
                {opt.textboxRequired === "yes" &&
                  (formState[question.questionId] || []).includes(opt.name) && (
                    <TextAreaField
                      rows={2}
                      placeholder="Please specify"
                      value={
                        formState[
                          `${question.questionId}_${opt.name}_textbox`
                        ] || ""
                      }
                    />
                  )}
              </div>
            ))}
          </div>
        </InputWrapper>
      )}

      {answerType === "textarea" && (
        <InputWrapper
          subTitle={question.text}
          title={categoryTitle}
          type={formatLabel(answerType)}
        >
          <TextAreaField
            rows={2}
            value={formState[question.questionId] || ""}
          />
        </InputWrapper>
      )}

      {answerType === "scale" && (
        <InputWrapper
          subTitle={question.text}
          title={categoryTitle}
          type={formatLabel(answerType)}
        >
          <SliderBar
            scale
            value={formState[question.questionId] || 0}
            customLabel
            min={parseInt(question.scale.min.key)}
            max={parseInt(question.scale.max.key)}
            leftLabel={question.scale.min.name}
            rightLabel={question.scale.max.name}
          />
        </InputWrapper>
      )}
    </div>
  );
};
// --- End QuestionItem Component ---

const ViewQuestionModal: FC<AddEditModalProps> = (props) => {
  const [formState, setFormState] = useState<{ [key: string]: any }>({});

  const { getCarePlan, carePlan, carePlanLoading } = useFetchCarePlan({
    templateId: props?.templateData?._id,
  }) as { getCarePlan: () => void; carePlan?: CarePlanAPIResponse; carePlanLoading: boolean };

  useEffect(() => {
    getCarePlan();
  }, [getCarePlan]); // Added getCarePlan to dependency array

  return (
    <Modal show={props.show} modalClassName={PatientStyle.customEnrollModal}>
      <ModalHeading
        heading={carePlan?.data.templateName}
        customClass={[PatientStyle.customEnrollHeading, "alignStart"].join(" ")}
        customCloseClass={PatientStyle.close_btn}
        closeIcon="close-cross"
        onClose={props.onClose}
      />
      {carePlanLoading ? (
        <SkeletonLoader />
      ) : (
        <ModalForm
          customClass={PatientStyle.enroll_ModalForm}
          id="confirm-form"
        >
          <div
            className={PatientStyle.enroll_ModalForm_inner}
            style={{ marginBottom: "1rem" }}
          >
            {carePlan?.data && (
              <>
                {carePlan.data.questionGroupedByCategory === "yes"
                  ? (carePlan.data.data as CategoryGroup[]).map((category) => (
                      <div
                        key={category._id}
                        className={PatientStyle.custom_content} 
                      >
                        {category.records.map((question, idx) => (
                          <QuestionItem
                            key={question._id}
                            question={question}
                            formState={formState}
                            categoryTitle={idx === 0 ? category.name : undefined}
                          />
                        ))}
                      </div>
                    ))
                  : (carePlan.data.data as BaseQuestion[]).map((question) => (
                      <QuestionItem
                        key={question._id}
                        question={question}
                        formState={formState}
                      />
                    ))}
              </>
            )}
          </div>
          <div className={PatientStyle.custom_btn_box}>
            <Button
              label="Cancel"
              icon="icon-close-cross"
              span={false}
              iconStyle={{ height: "1.5rem", width: "1.5rem" }}
              customClass={PatientStyle.cancel_btn}
              onClick={props.onClose}
              form="confirm-form"
            />
          </div>
        </ModalForm>
      )}
    </Modal>
  );
};

export default ViewQuestionModal;
